import {useAvailablePropertiesContext} from "components/src/hook/UseAvailablePropertiesContext";
import {useHasAllPermissions} from "components/src/hook/UseHasAllPermissions";
import {SettingsMenuBuilder} from "components/src/settings/SettingsMenuBuilder";
import {SideBar} from 'components/src/sidebar/SideBar'
import {
    AntennaSignal as SMSIcon,
    AtSign as MTAIcon,
    CardShield as SMIMEIcon,
    Dns as DomainsIcon,
    GoogleDocs as LogsIcon,
    Group as UsersIcon,
    Key as PGPIcon,
    Settings as SettingsIcon,
    ShieldEye as DLPIcon,
    StatsReport as DashboardIcon,
} from "iconoir-react";
import {useTranslation} from "react-i18next";

/**
 * AppSidebar component provides the main navigation sidebar of the application.
 * It includes various sections and links to different parts of the app such as
 * Users, Domains, S/MIME, PGP, MTA, and Settings.
 *
 * The sidebar is organized into sections and dropdown menus:
 * - Users: a direct link to the users' page.
 * - Domains: a direct link to the domains' page.
 * - S/MIME: a dropdown with links to Certificates, CRLs, and CA pages.
 * - PGP: a direct link to the PGP page.
 * - MTA: a dropdown with settings and queue management pages.
 * - Settings: a dropdown that contains dynamically generated settings menu
 * based on available global properties.
 *
 * The component uses the useAvailablePropertiesContext hook to access the available
 * properties required for building the dynamic settings menu.
 */
export const AppSidebar = () =>
{
    /**
     * All available user properties for which the currently logged in admin has permissions
     */
    const {availableProperties} = useAvailablePropertiesContext();
    /*
     * A helper for checking whether all required permissions are granted.
     */
    const hasAllPermissions = useHasAllPermissions();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();
    const {t:tu} = useTranslation('userproperties');

    return (
        <SideBar>
            <SideBar.Item title={t("sidebar.dashboard")} link="/" icon={<DashboardIcon/>}/>
            {hasAllPermissions(
                    [
                        "user:search-users",
                        "user:search-users-count"
                    ]) &&
                <SideBar.Item title={t("sidebar.users")} link="/users" icon={<UsersIcon/>}/>
            }

            {hasAllPermissions(
                    [
                        "domain:get-domains",
                        "domain:get-domains-count"
                    ]) &&
                <SideBar.Item title={t("sidebar.domains")} link="/domains" icon={<DomainsIcon/>}/>
            }

            <SideBar.Dropdown title={t("sidebar.smime")} icon={<SMIMEIcon/>}>
                {hasAllPermissions(
                        [
                            "certificate:get-certificates",
                            "certificate:get-certificates-count",
                            "certificate-validation:validate-certificate"
                        ]) &&
                    <SideBar.Item title={t("sidebar.certificates")} link="/certificates"/>
                }
                {hasAllPermissions(
                        [
                            "crl:get-crls",
                            "crl:get-crls-count",
                            "crl:validate-crl"
                        ]) &&
                    <SideBar.Item title={t("sidebar.crls")} link="/crls"/>
                }
                {hasAllPermissions(
                        [
                            "ca:get-pending-requests",
                            "ca:get-pending-requests-count"
                        ]) &&
                    <SideBar.Item title={t("sidebar.ca")} link="/ca"/>
                }
            </SideBar.Dropdown>

            {hasAllPermissions(
                    [
                        "pgp-keyring:get-keys",
                        "pgp-keyring:get-keys-count",
                        "pgp:is-user-id-revoked",
                        "pgp:is-user-id-valid"
                    ]) &&
                <SideBar.Item title={t("sidebar.pgp")} link="/pgp" icon={<PGPIcon/>}/>
            }

            <SideBar.Dropdown title={t("sidebar.dlp")} icon={<DLPIcon/>}>
                {hasAllPermissions(
                        [
                            "dlp:get-policy-patterns-count",
                            "dlp:get-policy-patterns"
                        ]) &&
                    <SideBar.Item title={t("sidebar.patterns")} link="/dlp/patterns"/>
                }
                {hasAllPermissions(
                        [
                            "dlp:get-policy-patterns-count",
                            "dlp:get-policy-patterns",
                            "dlp:get-global-policy-patterns"
                        ]) &&
                    <SideBar.Item title={t("sidebar.selection")} link="/dlp/selection"/>
                }
                {hasAllPermissions(
                        [
                            "quarantine:get-quarantined-mail",
                            "quarantine:get-quarantined-mail-count"
                        ]) &&
                    <SideBar.Item title={t("sidebar.quarantine")} link="/dlp/quarantine"/>
                }
            </SideBar.Dropdown>

            <SideBar.Dropdown title={t("sidebar.mta")} icon={<MTAIcon/>}>
                {hasAllPermissions(
                        [
                            "mta:get-standard-settings"
                        ]) &&
                    <SideBar.Item title={t("sidebar.settings")} link="/mta/settings"/>
                }
                <SideBar.Dropdown title={t("sidebar.queue")}>
                    {hasAllPermissions(
                            [
                                "mta:get-queue-size",
                                "mta:get-queue-list"
                            ]) &&
                        <SideBar.Item title={t("sidebar.mta")} link="/mta/queue/mta"/>
                    }
                    {hasAllPermissions(
                            [
                                "mail-queue:get-queue-size",
                                "mail-queue:get-queue-names",
                                "mail-queue:get-queued-mails"
                            ]) &&
                        <SideBar.Item title={t("sidebar.mpa")} link="/mta/queue/mpa"/>
                    }
                    {hasAllPermissions(
                            [
                                "mail-repository:get-mail-repository-urls",
                                "mail-repository:get-mail-keys"
                            ]) &&
                        <SideBar.Item title={t("sidebar.repository")} link="/mta/queue/repository"/>
                    }
                </SideBar.Dropdown>
                {hasAllPermissions(
                        [
                            "mta:map-list"
                        ]) &&
                    <SideBar.Item title={t("sidebar.maps")} link="/mta/maps"/>
                }
            </SideBar.Dropdown>

            {hasAllPermissions(
                    [
                        "log:read-log-count",
                        "log:read-log"
                    ]) &&
                <SideBar.Item title={t("sidebar.log")} link="/log" icon={<LogsIcon/>}/>
            }

            {hasAllPermissions(
                    [
                        "sms:get-sms-messages",
                        "sms:get-sms-queue-size"
                    ]) &&
                <SideBar.Item title={t("sidebar.sms")} link="/sms" icon={<SMSIcon/>}/>
            }

            <SideBar.Dropdown title={t("sidebar.settings")} icon={<SettingsIcon/>}>
                {SettingsMenuBuilder.buildMenu(availableProperties.globalProperties, "settings/system", tu)}
            </SideBar.Dropdown>
        </SideBar>
    )
}
